/**
 * 浏览器端 Python（Pyodide）：学生考试编程题「运行」优先走本机，不依赖服务器安装 Python。
 * 首次加载约 8–15MB（CDN），之后缓存；加载失败时由调用方回退服务端 /api/student/coding/run。
 */
const PYODIDE_VERSION = '0.26.4'
const PYODIDE_CDN_BASES = [
  `https://cdn.jsdelivr.net/pyodide/v${PYODIDE_VERSION}/full/`,
  `https://fastly.jsdelivr.net/pyodide/v${PYODIDE_VERSION}/full/`
]

/** 全页面单例：无论多少道 Python 题，只下载/初始化一次 Pyodide */
let loadPromise = null
let pyodideInstance = null
let lastLoadError = null
let resolvedIndexURL = null

function loadPyodideScript(indexURL) {
  const src = `${indexURL}pyodide.js`
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[data-exam-pyodide="1"]`)
    if (existing) {
      if (typeof window.loadPyodide === 'function') {
        resolve()
        return
      }
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('Pyodide 脚本加载失败')))
      return
    }
    const s = document.createElement('script')
    s.src = src
    s.async = true
    s.dataset.examPyodide = '1'
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('Pyodide 脚本加载失败'))
    document.head.appendChild(s)
  })
}

/**
 * 后台预加载 Pyodide（进入含编程题的考试时调用，可重复调用但不会重复下载）
 */
export function preloadPyodide() {
  if (pyodideInstance) return Promise.resolve(pyodideInstance)
  if (loadPromise) return loadPromise

  loadPromise = (async () => {
    let lastErr = null
    const bases = resolvedIndexURL ? [resolvedIndexURL] : PYODIDE_CDN_BASES
    for (const base of bases) {
      try {
        await loadPyodideScript(base)
        if (typeof window.loadPyodide !== 'function') {
          throw new Error('Pyodide 初始化接口不可用')
        }
        pyodideInstance = await window.loadPyodide({ indexURL: base })
        resolvedIndexURL = base
        lastLoadError = null
        return pyodideInstance
      } catch (e) {
        lastErr = e
        lastLoadError = e
      }
    }
    loadPromise = null
    throw lastErr || new Error('Pyodide 加载失败')
  })()

  return loadPromise
}

export function isPyodideReady() {
  return pyodideInstance != null
}

/** 是否正在首次加载（多题并发点击运行也只会走同一次加载） */
export function isPyodideLoading() {
  return loadPromise != null && pyodideInstance == null
}

export function getPyodideStatus() {
  return {
    ready: pyodideInstance != null,
    loading: isPyodideLoading(),
    indexURL: resolvedIndexURL
  }
}

export function getPyodideLoadError() {
  return lastLoadError
}

/**
 * 在浏览器中执行 Python，捕获 stdout / stderr。
 * @returns {Promise<{stdout:string, stderr:string, error:string, source:'pyodide'}>}
 */
export async function runPythonInBrowser(code, { timeoutMs = 10000 } = {}) {
  let pyodide
  try {
    pyodide = await preloadPyodide()
  } catch (e) {
    return {
      stdout: '',
      stderr: '',
      error: (e && e.message) || '浏览器 Python 环境加载失败',
      source: 'pyodide'
    }
  }

  let stdout = ''
  let stderr = ''

  const execTask = (async () => {
    pyodide.setStdout({ batched: (s) => { stdout += s } })
    pyodide.setStderr({ batched: (s) => { stderr += s } })
    try {
      await pyodide.runPythonAsync(code)
      return { stdout, stderr, error: '', source: 'pyodide' }
    } catch (e) {
      const msg = (e && e.message) ? String(e.message) : String(e)
      if (!stderr) stderr = msg
      else stderr += '\n' + msg
      return { stdout, stderr, error: '', source: 'pyodide' }
    }
  })()

  const timeoutTask = new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        stdout,
        stderr: stderr || '代码运行超时',
        error: `代码运行超过 ${Math.round(timeoutMs / 1000)} 秒已中止`,
        source: 'pyodide'
      })
    }, timeoutMs)
  })

  return Promise.race([execTask, timeoutTask])
}
